import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { DialogOnboardingPromoComponent } from "../common/dialog-onboarding-promo/dialog-onboarding-promo.component";
import { MarkPromoFlagSeenRequest, UserPromoFlagsService } from "src/swagger/api.client.generated";
import { finalize } from "rxjs/operators";
import { BlockingUiSignalService } from "./blocking-ui-signal.service";
import { FeatureFlagService } from "./feature-flag.service";
import { FeatureFlagKeys } from "./feature-flag.models";

const COMMUNITY_PROMO_FLAG_KEY = "CommunityPromo";

/**
 * Shows the "welcome to flip*" one-on-one onboarding promo (FLIP-144) at most once per
 * user: immediately after a new user finishes onboarding, or on an already-registered
 * user's next login/app open. Called from both onboarding-flow.component.ts (new users)
 * and app.component.ts (existing users).
 *
 * "Seen" is tracked server-side in UserPromoFlags (UserPromoFlagsController) rather than
 * localStorage, so it survives a logout/re-login - UserPromoFlags is a generic per-user
 * FlagKey table, so any future one-time promo/modal reuses this same service with a new key.
 *
 * Gated by the "community-promo" feature flag (FeatureFlags table, seeded ON) so it can be turned
 * off through the admin flag endpoints. While it is off nobody is marked as having seen it, so
 * turning it back on shows it to everyone who hasn't.
 */
@Injectable({ providedIn: "root" })
export class CommunityPromoModalService {
  constructor(
    private dialog: MatDialog,
    private userPromoFlagsService: UserPromoFlagsService,
    private blockingUiSignal: BlockingUiSignalService,
    private featureFlagService: FeatureFlagService
  ) {}

  maybeShow(): void {
    // Blocking from the start, flag wait included, so onboarding doesn't open first (FLIP-334).
    const releasePendingCheck = this.blockingUiSignal.registerPendingCheck();
    // init() is memoised, and app.component fires it un-awaited right before calling this - so wait
    // for it here rather than read isEnabled() against the still-empty (fail-closed) fallback.
    this.featureFlagService
      .init()
      .then(() => {
        if (!this.featureFlagService.isEnabled(FeatureFlagKeys.CommunityPromo)) {
          releasePendingCheck();
          return;
        }
        this.showIfUnseen(releasePendingCheck);
      })
      .catch(() => releasePendingCheck());
  }

  private showIfUnseen(releasePendingCheck: () => void): void {
    this.userPromoFlagsService.getSeenFlagKeys().pipe(finalize(releasePendingCheck)).subscribe({
      next: (response) => {
        const seenFlagKeys = response.responseData ?? [];
        if (seenFlagKeys.includes(COMMUNITY_PROMO_FLAG_KEY)) {
          return;
        }
        this.userPromoFlagsService
          .markFlagSeen(new MarkPromoFlagSeenRequest({ flagKey: COMMUNITY_PROMO_FLAG_KEY }))
          .subscribe();
        this.dialog.open(DialogOnboardingPromoComponent, {
          panelClass: "base-modal-panel",
          backdropClass: "base-modal-backdrop",
        });
      },
      // Non-critical: if the seen-flags check fails, skip showing the promo this time
      // rather than risk a broken check making it show on every app open.
      error: () => {},
    });
  }
}
